<script setup lang="ts">
import { provide, ref } from 'vue'
import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  VisuallyHidden,
} from 'reka-ui'
import { cn } from '@ds/lib/utils'
import { XIcon } from '@lucide/vue'
import { SIDEBAR_COLLAPSED } from '../../../components/DS/patterns/Sidebar/const'

type Props = {
  open: boolean
}

const { open } = defineProps<Props>()

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

/**
 * The drawer reuses `DSSidebarItem`/`DSSidebarGroup` for its nav content —
 * same as the desktop rail's expanded state — so it provides `collapsed` as
 * permanently false rather than duplicating their row markup.
 */
provide(SIDEBAR_COLLAPSED, ref(false))
</script>

<template>
  <DialogRoot
    :open
    @update:open="emit('update:open', $event)"
  >
    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 z-50 bg-scrim backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogContent
        :class="cn(
          'fixed inset-y-0 left-0 z-50 h-full w-79 max-w-[85vw]',
          'font-ds flex flex-col bg-sidebar text-primary-foreground',
          'shadow-ds-xl duration-ds-normal data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
        )"
      >
        <VisuallyHidden as-child>
          <DialogTitle>Menu</DialogTitle>
        </VisuallyHidden>
        <VisuallyHidden as-child>
          <DialogDescription>Navigation menu</DialogDescription>
        </VisuallyHidden>

        <div class="flex items-center justify-between h-15 shrink-0 pt-[env(safe-area-inset-top)] pl-4 pr-3">
          <slot name="logo" />
          <button
            type="button"
            class="flex items-center justify-center size-11 rounded-ds-md hover:bg-white/10 cursor-pointer"
            aria-label="Close menu"
            @click="emit('update:open', false)"
          >
            <XIcon
              class="size-5.5"
              aria-hidden="true"
            />
          </button>
        </div>
        <div
          v-if="$slots.top"
          class="px-3 pb-3.5"
        >
          <slot name="top" />
        </div>
        <nav
          aria-label="Primary"
          class="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-3 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20"
        >
          <slot />
        </nav>
        <div
          v-if="$slots.footer"
          class="flex shrink-0 flex-col gap-0.5 border-t border-white/15 p-3 pb-[max(2rem,env(safe-area-inset-bottom))]"
        >
          <slot name="footer" />
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
